// The walk between hunts. When your hunt moves to another area, the hunt scene turns into the region map for a
// moment: your trainer and lead creature walk the painted trail (client/regionmap.ts) from the old area to the new
// one, and the new area's name comes up as they arrive. Entering a region walks in from the end of its trail with
// the region's name over the map. A click skips straight to the new hunt; with reduced motion only the name shows.
import { AREA, REGIONS, type AreaDef } from '../shared/grounds.ts';
import { paintRegionMap, regionPoints, regionTrail } from './regionmap.ts';
import { sheet, sheetInfo, sheetCell, drawable as ready } from './assets.ts';

type Pt = { x: number; y: number };
type Facing = 'front' | 'left' | 'back' | 'right';
/** A walker's sheet: the trainer's outfit, or the lead's art id. */
export interface Walker { id: string; aura: boolean }

/** The painted map in map pixels, the shape of the map page's box. */
const MW = 320, MH = 236;
/** CSS pixels per map pixel: a little closer in than the map page (3). */
const SCALE = 4;
/** CSS pixels per sheet pixel for the walkers, so a trainer stands about as tall as a painted tree. */
const SPR = 0.75;
const ROW: Record<Facing, number> = { front: 0, left: 1, back: 2, right: 3 };
const WALK_MS = 140, IDLE_MS = 150;
/** Fade in, stand at the new area with its name up, fade out to the hunt. */
const IN_MS = 200, HOLD_MS = 850, OUT_MS = 420;
/** Reduced motion: the area's name over the new hunt, nothing moving. */
const STILL_MS = 1900;
/** Where the name cards sit, as a share of the canvas below any hunt card: clear of the hunt card above and the panels below. */
const CARD_Y = 0.22;
/** How far ahead of the trainer the lead walks, in map pixels. */
const GAP = 7;

interface Travel {
  dest: AreaDef;
  /** Region name card when the walk enters a new region. */
  entering: boolean;
  still: boolean;
  map: HTMLCanvasElement;
  sea: string;
  nodes: Pt[];
  route: Pt[];
  cum: number[];
  t0: number;
  walkMs: number;
  face: { trainer: Facing; lead: Facing };
}
let tr: Travel | null = null;

const maps = new Map<string, { c: HTMLCanvasElement; sea: string }>();
function regionMap(region: string) {
  let m = maps.get(region);
  if (!m) {
    const c = paintRegionMap(region, regionPoints(region), MW, MH);
    const p = c.getContext('2d')!.getImageData(0, 0, 1, 1).data;
    m = { c, sea: `rgb(${p[0]},${p[1]},${p[2]})` };
    maps.set(region, m);
  }
  return m;
}

const regionIndex = (id: string) => REGIONS.findIndex((r) => r.id === id);
const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));

/** The trail from one area to another, in map pixels; entering a region starts just outside the end of its trail. */
function routeFor(from: AreaDef | undefined, dest: AreaDef): { route: Pt[]; nodes: Pt[] } {
  const pts = regionPoints(dest.region);
  const { path, at } = regionTrail(dest.region, pts, MW, MH);
  const nodes = pts.map((p) => ({ x: p.x * MW, y: p.y * MH }));
  const leg = (a: number, b: number) => (a <= b ? path.slice(a, b + 1) : path.slice(b, a + 1).reverse());
  if (from && from.region === dest.region) return { route: leg(at[from.index], at[dest.index]), nodes };
  const fromStart = !from || regionIndex(from.region) < regionIndex(dest.region);
  const end = fromStart ? 0 : nodes.length - 1, next = fromStart ? 1 : nodes.length - 2;
  const e = nodes[end], n = nodes[next] ?? { x: e.x, y: e.y + 1 };
  const dx = e.x - n.x, dy = e.y - n.y, l = Math.hypot(dx, dy) || 1;
  const start = { x: clamp(e.x + (dx / l) * 24, 6, MW - 6), y: clamp(e.y + (dy / l) * 24, 10, MH - 4) };
  return { route: [start, ...leg(at[end], at[dest.index])], nodes };
}

/** Starts the walk when the hunt's map changes from one area to another. Map keys are area ids (shared/worldmap.ts). */
export function startTravel(fromKey: string, toKey: string, reduce: boolean) {
  const dest = AREA[toKey];
  if (!dest || fromKey === toKey) { tr = null; return; }
  const from = AREA[fromKey];
  const entering = !from || from.region !== dest.region;
  const now = performance.now();
  if (reduce) {
    tr = { dest, entering, still: true, map: null!, sea: '', nodes: [], route: [], cum: [0], t0: now, walkMs: 0, face: { trainer: 'front', lead: 'front' } };
    return;
  }
  const { c, sea } = regionMap(dest.region);
  const { route, nodes } = routeFor(from, dest);
  const cum = [0];
  for (let i = 1; i < route.length; i++) cum.push(cum[i - 1] + Math.hypot(route[i].x - route[i - 1].x, route[i].y - route[i - 1].y));
  const len = cum[cum.length - 1];
  // Entering a region walks long enough for its name to come and go before the area's.
  const walkMs = clamp(600 + len * 16, entering ? 2000 : 1000, 2400);
  tr = { dest, entering, still: false, map: c, sea, nodes, route, cum, t0: now, walkMs, face: { trainer: 'front', lead: 'front' } };
}

/** Name cards shrink on a short field. */
const cardFit = (cssH: number) => clamp(cssH / 520, 0.62, 1);
const ends = (x: Travel) => (x.still ? STILL_MS : IN_MS + x.walkMs + HOLD_MS + OUT_MS);
const outAt = (x: Travel) => IN_MS + x.walkMs + HOLD_MS;

/** Whether the map hides the hunt now (the scene isn't drawn and clicks don't reach it). */
export function travelCovers(t = performance.now()): boolean {
  if (!tr || tr.still) return false;
  const age = t - tr.t0;
  if (age > ends(tr)) { tr = null; return false; }
  return age < outAt(tr);
}

/** A click on the scene during the walk: jump to the new hunt. True when it skipped. */
export function skipTravel(): boolean {
  if (!travelCovers()) return false;
  tr!.t0 = performance.now() - outAt(tr!);
  return true;
}

/** Point and direction at distance d along the route. Facing reads the trail a few pixels either side, so small wiggles don't flip it. */
function along(x: Travel, d: number): { p: Pt; dx: number; dy: number } {
  const pa = at(x, d - 4), pb = at(x, d + 4);
  return { p: at(x, d), dx: pb.x - pa.x, dy: pb.y - pa.y };
}
function at(x: Travel, d: number): Pt {
  const { route, cum } = x, len = cum[cum.length - 1];
  d = clamp(d, 0, len);
  let i = 1;
  while (i < route.length - 1 && cum[i] < d) i++;
  const a = route[i - 1] ?? route[0], b = route[i] ?? a, seg = cum[i] - cum[i - 1] || 1, k = clamp((d - cum[i - 1]) / seg, 0, 1);
  return { x: a.x + (b.x - a.x) * k, y: a.y + (b.y - a.y) * k };
}
function faceFor(prev: Facing, dx: number, dy: number): Facing {
  if (Math.hypot(dx, dy) < 0.5) return prev;
  // Sideways wins near the diagonal: walking sprites read best side-on.
  return Math.abs(dx) > Math.abs(dy) * 0.7 ? (dx > 0 ? 'right' : 'left') : dy > 0 ? 'front' : 'back';
}

let fonts: { display: string; caps: string } | null = null;
function fontsNow() {
  if (!fonts) {
    const cs = getComputedStyle(document.documentElement);
    fonts = { display: cs.getPropertyValue('--display').trim() || 'Georgia, serif', caps: cs.getPropertyValue('--pixel').trim() || 'Georgia, serif' };
  }
  return fonts;
}

/**
 * Draws the walk over the canvas (call last in the frame; while travelCovers() it is the whole frame).
 * `top` is canvas pixels down to the bottom of the hunt card: the names go below it.
 */
export function drawTravel(g: CanvasRenderingContext2D, W: number, H: number, dpr: number, t: number, top: number, trainer: Walker | null, lead: Walker | null) {
  const x = tr;
  if (!x) return;
  const age = t - x.t0;
  if (age > ends(x)) { tr = null; return; }
  g.save();
  g.imageSmoothingEnabled = false;
  if (x.still) {
    const a = age < 250 ? age / 250 : age > STILL_MS - 400 ? (STILL_MS - age) / 400 : 1;
    plaque(g, W / 2, top + (H - top) * CARD_Y, x, Math.max(0, a), dpr * cardFit(H / dpr));
    g.restore();
    return;
  }
  const out = outAt(x);
  const alpha = age < IN_MS ? age / IN_MS : age > out ? Math.max(0, 1 - (age - out) / OUT_MS) : 1;
  // A short field (phones) shows more of the map, with smaller walkers and names.
  const fit = clamp(H / dpr / 420, 0.6, 1), u = dpr * cardFit(H / dpr);
  const s = SCALE * fit * dpr, len = x.cum[x.cum.length - 1];
  const walkK = clamp((age - IN_MS) / x.walkMs, 0, 1);
  // Ease in and out so the walk starts and stops like steps, not a slide.
  const d = (walkK < 0.5 ? 2 * walkK * walkK : 1 - Math.pow(-2 * walkK + 2, 2) / 2) * len;
  const moving = walkK > 0 && walkK < 1;
  const tp = along(x, d), lpos = along(x, Math.min(len, d + GAP));
  x.face.trainer = moving ? faceFor(x.face.trainer, tp.dx, tp.dy) : walkK >= 1 ? 'front' : x.face.trainer;
  x.face.lead = moving ? faceFor(x.face.lead, lpos.dx, lpos.dy) : walkK >= 1 ? 'front' : x.face.lead;

  // Camera: the trainer just below the middle of the uncovered part (the name card goes above), never past the map's edge.
  const viewW = W / s, viewH = H / s, topM = top / s;
  const want = { x: tp.p.x - viewW / 2, y: tp.p.y - (topM + (viewH - topM) * 0.54) };
  const camX = viewW >= MW ? (MW - viewW) / 2 : clamp(want.x, 0, MW - viewW);
  const camY = viewH >= MH ? (MH - viewH) / 2 : clamp(want.y, -topM, MH - viewH);
  const X = (mx: number) => Math.round((mx - camX) * s), Y = (my: number) => Math.round((my - camY) * s);

  g.globalAlpha = alpha;
  g.fillStyle = x.sea;
  g.fillRect(0, 0, W, H);
  g.drawImage(x.map, X(0), Y(0), Math.round(MW * s), Math.round(MH * s));

  // The route still ahead, in gilt steps along the trail.
  const step = 6, r = Math.round(s * 0.75), o = Math.max(1, Math.round(dpr));
  for (let k = Math.ceil((d + GAP + 4) / step) * step; k < len - 4; k += step) {
    const p = at(x, k);
    g.fillStyle = '#2a1d10';
    g.fillRect(X(p.x) - r, Y(p.y) - r, r * 2, r * 2);
    g.fillStyle = '#f0d68a';
    g.fillRect(X(p.x) - r + o, Y(p.y) - r + o, r * 2 - 2 * o, r * 2 - 2 * o);
  }
  // Waystones at every area; the new one rings gold.
  x.nodes.forEach((n, i) => {
    const dest = i === x.dest.index, r = (dest ? 3.4 : 2.4) * s;
    g.fillStyle = '#2a1d10';
    g.beginPath(); g.arc(X(n.x), Y(n.y), r + 1.2 * dpr, 0, Math.PI * 2); g.fill();
    g.fillStyle = dest ? '#e8c76a' : '#d6c5a1';
    g.beginPath(); g.arc(X(n.x), Y(n.y), r, 0, Math.PI * 2); g.fill();
    if (dest) {
      const pulse = (t % 1100) / 1100;
      g.strokeStyle = `rgba(240,214,138,${(1 - pulse) * 0.9})`;
      g.lineWidth = 2 * dpr;
      g.beginPath(); g.arc(X(n.x), Y(n.y), r + (2 + pulse * 7) * s, 0, Math.PI * 2); g.stroke();
    }
  });

  // The walkers, back to front.
  const ws: { w: Walker; p: Pt; face: Facing; seed: number }[] = [];
  if (lead) ws.push({ w: lead, p: lpos.p, face: x.face.lead, seed: 700 });
  if (trainer) ws.push({ w: trainer, p: tp.p, face: x.face.trainer, seed: 0 });
  ws.sort((a, b) => a.p.y - b.p.y);
  for (const w of ws) {
    const info = sheetInfo(w.w.id), img = info && sheet(w.w.id, w.w.aura);
    if (!info || !img || !ready(img)) continue;
    const n = info.size, p = sheetCell(info), k = SPR * fit * dpr, size = Math.round(n * k);
    const walking = moving && !(w.seed && d + GAP >= len);
    const col = walking ? Math.floor(t / WALK_MS) % info.walk : info.walk + (Math.floor((t + w.seed) / IDLE_MS) % info.idle);
    const fx = X(w.p.x), fy = Y(w.p.y);
    g.fillStyle = 'rgba(0,0,0,.28)';
    g.beginPath(); g.ellipse(fx, fy, size * 0.2, size * 0.06, 0, 0, Math.PI * 2); g.fill();
    g.drawImage(img, col * p, ROW[w.face] * p, p, p, Math.round(fx - size / 2), Math.round(fy + 7 * k - size), size, size);
  }

  // Entering a region: its name over the map for the first part of the walk, then the area's as the walkers arrive.
  const cy = top + (H - top) * CARD_Y, walked = age - IN_MS;
  if (x.entering) {
    const ra = walked < 150 ? 0 : walked < 500 ? (walked - 150) / 350 : walked < 1250 ? 1 : Math.max(0, 1 - (walked - 1250) / 350);
    if (ra > 0) regionTitle(g, W / 2, cy, x.dest.region, alpha * ra, u);
  }
  const from = x.entering ? Math.max(0.7 * x.walkMs, 1650) : 0.7 * x.walkMs;
  const pa = clamp((walked - from) / 300, 0, 1);
  if (pa > 0) plaque(g, W / 2, cy, x, alpha * pa, u);
  g.restore();
}

/** A vellum plaque with the region in small capitals over the area's name, like the hunt card. */
function plaque(g: CanvasRenderingContext2D, cx: number, cy: number, x: Travel, alpha: number, dpr: number) {
  const f = fontsNow(), region = REGIONS.find((r) => r.id === x.dest.region)?.name ?? '';
  g.globalAlpha = alpha;
  g.textAlign = 'center';
  g.textBaseline = 'alphabetic';
  g.font = `600 ${Math.round(26 * dpr)}px ${f.display}`;
  const w = Math.max(g.measureText(x.dest.name).width + 56 * dpr, 200 * dpr), h = 64 * dpr;
  const l = Math.round(cx - w / 2), tp = Math.round(cy - h / 2);
  g.fillStyle = 'rgba(0,0,0,.35)';
  g.fillRect(l + 3 * dpr, tp + 4 * dpr, w, h);
  const grad = g.createLinearGradient(0, tp, 0, tp + h);
  grad.addColorStop(0, '#dccdab'); grad.addColorStop(1, '#c6b088');
  g.fillStyle = grad;
  g.fillRect(l, tp, w, h);
  g.strokeStyle = '#3a2a1c'; g.lineWidth = 2 * dpr;
  g.strokeRect(l + dpr, tp + dpr, w - 2 * dpr, h - 2 * dpr);
  g.strokeStyle = '#b39a66'; g.lineWidth = dpr;
  g.strokeRect(l + 5.5 * dpr, tp + 5.5 * dpr, w - 11 * dpr, h - 11 * dpr);
  // Gilt studs at the corners.
  g.fillStyle = '#b39a66';
  for (const [px, py] of [[l + 5.5 * dpr, tp + 5.5 * dpr], [l + w - 5.5 * dpr, tp + 5.5 * dpr], [l + 5.5 * dpr, tp + h - 5.5 * dpr], [l + w - 5.5 * dpr, tp + h - 5.5 * dpr]]) {
    g.beginPath(); g.moveTo(px, py - 3.5 * dpr); g.lineTo(px + 3.5 * dpr, py); g.lineTo(px, py + 3.5 * dpr); g.lineTo(px - 3.5 * dpr, py); g.closePath(); g.fill();
  }
  g.fillStyle = '#6a5236';
  g.font = `600 ${Math.round(12 * dpr)}px ${f.caps}`;
  spaced(g, region.toUpperCase(), cx, tp + 22 * dpr, 2.5 * dpr);
  g.fillStyle = '#33261a';
  g.font = `600 ${Math.round(26 * dpr)}px ${f.display}`;
  g.fillText(x.dest.name, cx, tp + 50 * dpr);
  g.globalAlpha = 1;
}

/** The region's name in large letters over the map, ruled in gilt. */
function regionTitle(g: CanvasRenderingContext2D, cx: number, cy: number, region: string, alpha: number, dpr: number) {
  const f = fontsNow(), r = REGIONS.find((x) => x.id === region);
  if (!r) return;
  g.globalAlpha = alpha;
  g.textAlign = 'center';
  g.textBaseline = 'alphabetic';
  g.lineJoin = 'round';
  g.font = `600 ${Math.round(46 * dpr)}px ${f.display}`;
  const tw = g.measureText(r.name).width;
  g.lineWidth = 6 * dpr; g.strokeStyle = 'rgba(26,18,10,.9)';
  const by = cy + 4 * dpr;
  g.strokeText(r.name, cx, by);
  g.fillStyle = '#f2e3bd';
  g.fillText(r.name, cx, by);
  // Gilt rules either side of the subtitle.
  const ry = by + 22 * dpr, half = tw / 2 + 18 * dpr;
  g.fillStyle = 'rgba(26,18,10,.85)';
  g.fillRect(Math.round(cx - half - dpr), Math.round(ry - 2 * dpr), Math.round(half * 2 + 2 * dpr), Math.round(4 * dpr));
  g.fillStyle = '#c9a95e';
  g.fillRect(Math.round(cx - half), Math.round(ry - dpr), Math.round(half * 2), Math.round(2 * dpr));
  g.font = `600 ${Math.round(13 * dpr)}px ${f.caps}`;
  const sub = r.mythology.toUpperCase();
  const sw = g.measureText(sub).width + sub.length * 3 * dpr + 16 * dpr;
  g.fillStyle = 'rgba(26,18,10,.9)';
  g.fillRect(Math.round(cx - sw / 2), Math.round(ry - 10 * dpr), Math.round(sw), Math.round(20 * dpr));
  g.fillStyle = '#e8d29a';
  spaced(g, sub, cx, ry + 5 * dpr, 3 * dpr);
  g.globalAlpha = 1;
}

/** Centred text with letter spacing (canvas letterSpacing where the browser has it). */
function spaced(g: CanvasRenderingContext2D, s: string, cx: number, y: number, sp: number) {
  const c = g as CanvasRenderingContext2D & { letterSpacing?: string };
  if ('letterSpacing' in c) {
    c.letterSpacing = `${sp}px`;
    // Trailing spacing shifts centred text right by half a gap.
    g.fillText(s, cx + sp / 2, y);
    c.letterSpacing = '0px';
    return;
  }
  g.fillText(s, cx, y);
}
